import React from "react";

import { visibleDashboardTimeRangeOptions } from "../../services/helpers/DashboardHelpers";
import { DashboardTimeRangePills } from "../Dashboard/shared/DashboardTimeRangePills";
import { MID_FI_FIELD_CLASS, MidFiFieldLabel } from "./DetailFilterBar";

interface IProps {
  timeRange: string;
  compareMode: string;
  onTimeRangeChange: (value: string) => void;
  onCompareModeChange: (value: string) => void;
  unavailableTimeRanges?: string[] | null;
  /** "midfi": pill group + labelled Compare select for the 4019 filter bar. Kept separate from the
   * shared DashboardTimeRangePills, which the main dashboard also renders. */
  variant?: "default" | "midfi";
}

const COMPARE_MODE_OPTIONS = [
  { value: "PriorPeriod", label: "vs Prior Period" },
  { value: "YearOverYear", label: "vs Same Period Last Year" },
];

// Shared with KpiStrip so every delta on the page declares the same basis label as this control,
// per the wireframe's cross-sheet convention ("comparison basis is a global, visible control").
export const COMPARE_MODE_LABELS: Record<string, string> = Object.fromEntries(
  COMPARE_MODE_OPTIONS.map((option) => [option.value, option.label])
);

const MidFiControls = ({
  timeRange,
  compareMode,
  onTimeRangeChange,
  onCompareModeChange,
  unavailableTimeRanges,
}: IProps) => (
  <>
    <div className="flex rounded-[22px] border border-mf-border bg-white p-[3px]" role="group" aria-label="Time range">
      {visibleDashboardTimeRangeOptions(unavailableTimeRanges).map((range) => {
        const active = timeRange === range.value;
        return (
          <button
            key={range.value}
            type="button"
            aria-pressed={active}
            onClick={() => onTimeRangeChange(range.value)}
            className={`rounded-[18px] px-3.5 py-1.5 text-[12.5px] font-bold transition ${
              active
                ? "text-mf-ink shadow-[inset_0_0_0_1.5px_#25272E]"
                : "text-mf-sub hover:text-mf-ink"
            }`}
          >
            {range.label}
          </button>
        );
      })}
    </div>
    <label className={`${MID_FI_FIELD_CLASS} text-mf-ink`}>
      <MidFiFieldLabel>Compare</MidFiFieldLabel>
      <select
        className="cursor-pointer bg-transparent outline-none"
        value={compareMode}
        onChange={(event) => onCompareModeChange(event.target.value)}
      >
        {COMPARE_MODE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  </>
);

export const NextGenChartControls = (props: IProps) => {
  const {
    timeRange,
    compareMode,
    onTimeRangeChange,
    onCompareModeChange,
    unavailableTimeRanges,
    variant = "default",
  } = props;

  if (variant === "midfi") {
    return <MidFiControls {...props} />;
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <DashboardTimeRangePills
        timeRange={timeRange}
        onTimeRangeChange={onTimeRangeChange}
        unavailableTimeRanges={unavailableTimeRanges}
        showLabel
      />

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Compare</span>
          <select
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
            value={compareMode}
            onChange={(event) => onCompareModeChange(event.target.value)}
          >
            {COMPARE_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
